import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from 'react-query';
import { PlusIcon } from '@heroicons/react/24/outline';
import api from '../../services/api.ts';
import { formatCurrency } from '../../utils/currency.ts';
import { useAuth } from '../../contexts/AuthContext.tsx';
import PageHeader from '../../components/ui/PageHeader.tsx';
import StatusChip from '../../components/ui/StatusChip.tsx';
import { formatDateOnly, parseDateOnly } from '../../utils/date.ts';
import ConfirmationModal from '../../components/ConfirmationModal.tsx';
import StackCell from '../../components/ui/StackCell.tsx';

type Tab = 'pagar' | 'receber';

interface Row {
  id: string;
  description: string;
  counterparty: string;
  amount: number;
  remainingAmount: number;
  dueDate: string;
  status: string;
  installments?: number;
  currentInstallment?: number;
}

const chipPagar = (status: string, dueDate: string) => {
  const due = parseDateOnly(dueDate) || new Date(0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (status === 'paid') return { label: 'Pago', tone: 'green' as const };
  if (status === 'overdue' || (status === 'active' && due < today))
    return { label: 'Vencido', tone: 'red' as const };
  if (status === 'active' && due.getTime() - today.getTime() > 30 * 86400000)
    return { label: 'Agendado', tone: 'slate' as const };
  return { label: 'Em aberto', tone: 'amber' as const };
};

const chipReceber = (status: string, dueDate: string) => {
  const due = parseDateOnly(dueDate) || new Date(0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (status === 'paid') return { label: 'Recebido', tone: 'green' as const };
  if (status === 'overdue' || (status === 'active' && due < today))
    return { label: 'Atrasado', tone: 'red' as const };
  if (status === 'active' && due.getTime() - today.getTime() > 30 * 86400000)
    return { label: 'Agendado', tone: 'slate' as const };
  return { label: 'Em aberto', tone: 'amber' as const };
};

const Contas: React.FC = () => {
  const [params, setParams] = useSearchParams();
  const tab = (params.get('tab') === 'receber' ? 'receber' : 'pagar') as Tab;
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState<string | number | null>(null);
  const [deleting, setDeleting] = useState(false);

  const setTab = (next: Tab) => {
    setParams({ tab: next });
    setStatusFilter('all');
    setSearch('');
  };

  const { data: payables = [], isLoading: loadingPagar, error: errPagar } = useQuery(
    'contas-a-pagar',
    async () => {
      const res = await api.get('/financeiro/contas-a-pagar');
      return (res.data as any[]).map(i => ({
        id: i.id,
        description: i.description,
        counterparty: i.creditor,
        amount: i.amount,
        remainingAmount: i.remainingAmount,
        dueDate: i.dueDate,
        status: i.status,
        installments: i.installments,
        currentInstallment: i.currentInstallment,
      })) as Row[];
    },
    { enabled: tab === 'pagar' }
  );

  const { data: receivables = [], isLoading: loadingReceber, error: errReceber } = useQuery(
    'contas-a-receber',
    async () => {
      const res = await api.get('/financeiro/contas-a-receber');
      return (res.data as any[]).map(i => ({
        id: i.id,
        description: i.description,
        counterparty: i.customer,
        amount: i.amount,
        remainingAmount: i.remainingAmount,
        dueDate: i.dueDate,
        status: i.status,
        installments: i.installments,
        currentInstallment: i.currentInstallment,
      })) as Row[];
    },
    { enabled: tab === 'receber' }
  );

  const items = tab === 'pagar' ? payables : receivables;
  const isLoading = tab === 'pagar' ? loadingPagar : loadingReceber;
  const error = tab === 'pagar' ? errPagar : errReceber;
  const chipFn = tab === 'pagar' ? chipPagar : chipReceber;

  const filtered = useMemo(() => {
    return items.filter(item => {
      const chip = chipFn(item.status, item.dueDate);
      const matchesSearch =
        item.description.toLowerCase().includes(search.toLowerCase()) ||
        (item.counterparty || '').toLowerCase().includes(search.toLowerCase());
      const matchesStatus =
        statusFilter === 'all' ||
        (statusFilter === 'open' && (chip.label === 'Em aberto' || chip.label === 'Agendado')) ||
        (statusFilter === 'overdue' && (chip.label === 'Vencido' || chip.label === 'Atrasado')) ||
        (statusFilter === 'done' && (chip.label === 'Pago' || chip.label === 'Recebido'));
      return matchesSearch && matchesStatus;
    });
  }, [items, search, statusFilter, chipFn]);

  const openItems = items.filter(i => i.status !== 'paid');
  const saldo = openItems.reduce((s, i) => s + Number(i.remainingAmount || 0), 0);
  const overdue = items.filter(i => {
    const l = chipFn(i.status, i.dueDate).label;
    return l === 'Vencido' || l === 'Atrasado';
  });
  const overdueAmt = overdue.reduce((s, i) => s + Number(i.remainingAmount || 0), 0);
  const doneAmt = items
    .filter(i => i.status === 'paid')
    .reduce((s, i) => s + (Number(i.amount) - Number(i.remainingAmount) || Number(i.amount)), 0);

  const canCreate =
    tab === 'pagar' ? hasPermission('debts.create') : hasPermission('receivables.create');
  const canDelete =
    tab === 'pagar' ? hasPermission('debts.delete') : hasPermission('receivables.delete');
  const canSettle =
    tab === 'pagar' ? hasPermission('debts.update') : hasPermission('receivables.update');

  const novaHref =
    tab === 'pagar' ? '/financeiro/contas/pagar/nova' : '/financeiro/contas/receber/nova';
  const settleHref = (id: string) =>
    tab === 'pagar'
      ? `/financeiro/contas/pagar/${id}/pagar`
      : `/financeiro/contas/receber/${id}/receber`;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="field-error">
        Erro ao carregar contas a {tab === 'pagar' ? 'pagar' : 'receber'}.
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        breadcrumb="Financeiro / Contas"
        title="Contas"
        subtitle="Ainda falta · a pagar e a receber"
        actions={
          canCreate ? (
            <button
              type="button"
              className="btn btn-primary btn-block-mobile"
              onClick={() => navigate(novaHref)}
            >
              <PlusIcon style={{ width: 18, height: 18 }} />
              {tab === 'pagar' ? 'Nova conta a pagar' : 'Nova conta a receber'}
            </button>
          ) : undefined
        }
      />

      <div className="tabs">
        <button
          type="button"
          className={`tab${tab === 'pagar' ? ' active' : ''}`}
          onClick={() => setTab('pagar')}
        >
          A pagar
          {payables.filter(i => i.status !== 'paid').length > 0 && tab === 'pagar' && (
            <span className="badge-count">{openItems.length}</span>
          )}
        </button>
        <button
          type="button"
          className={`tab${tab === 'receber' ? ' active' : ''}`}
          onClick={() => setTab('receber')}
        >
          A receber
        </button>
      </div>

      <div className="toolbar">
        <div className="filters">
          <label className="filter-field">
            <span className="filter-label">Status</span>
            <select
              className="select"
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
            >
              <option value="all">Todos</option>
              <option value="open">Em aberto</option>
              <option value="overdue">{tab === 'pagar' ? 'Vencido' : 'Atrasado'}</option>
              <option value="done">{tab === 'pagar' ? 'Pago' : 'Recebido'}</option>
            </select>
          </label>
          <label className="filter-field" style={{ flex: 1 }}>
            <span className="filter-label">Buscar</span>
            <input
              className="input search"
              placeholder={tab === 'pagar' ? 'Fornecedor ou descrição' : 'Cliente ou descrição'}
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </label>
        </div>
      </div>

      <div className="kpi-row" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
        <div className="kpi">
          <div className="kpi-label">{tab === 'pagar' ? 'Saldo a pagar' : 'Saldo a receber'}</div>
          <div className={`kpi-value ${tab === 'pagar' ? 'negative' : 'positive'}`}>
            {formatCurrency(saldo)}
          </div>
          <div className="kpi-hint">{openItems.length} em aberto</div>
        </div>
        <div className="kpi">
          <div className="kpi-label">{tab === 'pagar' ? 'Vencidas' : 'Atrasadas'}</div>
          <div className="kpi-value" style={{ color: 'var(--amber-700)' }}>
            {formatCurrency(overdueAmt)}
          </div>
          <div className="kpi-hint">{overdue.length} conta(s)</div>
        </div>
        <div className="kpi">
          <div className="kpi-label">{tab === 'pagar' ? 'Pagas no mês' : 'Recebidas no mês'}</div>
          <div className="kpi-value positive">{formatCurrency(doneAmt)}</div>
          <div className="kpi-hint">já no fluxo de caixa</div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <div>
            <div className="card-title">
              {tab === 'pagar' ? 'Contas a pagar' : 'Contas a receber'}
            </div>
            <span className="muted">
              {tab === 'pagar'
                ? 'Agenda do que ainda falta pagar'
                : 'Agenda do que ainda falta receber'}
            </span>
          </div>
        </div>
        {filtered.length === 0 ? (
          <div style={{ padding: 48, textAlign: 'center' }} className="muted">
            Nenhuma conta encontrada.
          </div>
        ) : (
          <div className="table-wrap">
            <table className="data-table compact-mobile">
              <thead>
                <tr>
                  <th>Descrição</th>
                  <th className="hide-m">Vencimento</th>
                  <th className="hide-m">Parcela</th>
                  <th className="hide-m">Status</th>
                  <th className="hide-m" style={{ textAlign: 'right' }}>Valor</th>
                  <th style={{ textAlign: 'right' }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(item => {
                  const chip = chipFn(item.status, item.dueDate);
                  const parcela =
                    item.installments && item.installments > 1
                      ? `${String(item.currentInstallment || 1).padStart(2, '0')}/${String(item.installments).padStart(2, '0')}`
                      : 'Avulsa';
                  const done = chip.label === 'Pago' || chip.label === 'Recebido';
                  return (
                    <tr key={item.id}>
                      <td>
                        <StackCell
                          title={item.description}
                          meta={item.counterparty || undefined}
                          stackExtra={
                            <>
                              {formatDateOnly(item.dueDate)} · {parcela} · {chip.label} ·{' '}
                              {formatCurrency(done ? item.amount : item.remainingAmount)}
                            </>
                          }
                        />
                      </td>
                      <td className="mono hide-m">
                        {formatDateOnly(item.dueDate)}
                      </td>
                      <td className="hide-m">{parcela}</td>
                      <td className="hide-m">
                        <StatusChip label={chip.label} tone={chip.tone} />
                      </td>
                      <td className="amount hide-m" style={{ textAlign: 'right' }}>
                        {formatCurrency(done ? item.amount : item.remainingAmount)}
                      </td>
                      <td>
                        <div className="row-actions">
                          <Link
                            className="link-btn action-open"
                            to={
                              tab === 'pagar'
                                ? `/financeiro/contas-a-pagar/${item.id}`
                                : `/receivables/${item.id}`
                            }
                          >
                            Abrir
                          </Link>
                          {canDelete && (
                            <button
                              type="button"
                              className="link-btn action-keep action-danger"
                              onClick={() => { setDeleteId(item.id); setShowDeleteModal(true); }}
                            >
                              Excluir
                            </button>
                          )}
                          {!done && canSettle && (
                            <Link className="link-btn action-secondary" to={settleHref(item.id)}>
                              {tab === 'pagar' ? 'Pagar' : 'Receber'}
                            </Link>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => { setShowDeleteModal(false); setDeleteId(null); }}
        title={tab === 'pagar' ? 'Excluir conta a pagar' : 'Excluir conta a receber'}
        message="Tem certeza que deseja excluir este lançamento? Esta ação não pode ser desfeita."
        onConfirm={async () => {
          if (deleteId == null) return;
          setDeleting(true);
          try {
            if (tab === 'pagar') {
              await api.delete(`/debts/${deleteId}`);
              queryClient.invalidateQueries(['contas-a-pagar']);
            } else {
              await api.delete(`/receivables/${deleteId}`);
              queryClient.invalidateQueries(['contas-a-receber']);
            }
          } finally {
            setDeleting(false);
            setShowDeleteModal(false);
            setDeleteId(null);
          }
        }}
        confirmText="Excluir"
        cancelText="Cancelar"
        type="danger"
        isLoading={deleting}
      />
    </div>
  );
};

export default Contas;
